import { ArrowDown } from 'lucide-react';
import { PlatformMark } from '../PlatformMark';
import { useAuth } from '../../hooks/useAuth';
import '../ProjectStepper/styles.css';
import './styles.css';

/*
 * The signed-out home page: the loop shown happening, not described.
 *
 * Video 1's storyboard strip ends on its report verdict, in red marker, and
 * that line arrives as Video 2's first note. It is the product's own strip
 * with sample content, labelled as a sample: there are no testimonials,
 * customers or figures to show, and none are invented. Direction contract:
 * .impeccable/surfaces/ui-mcapn-src-components-unauthenticatedapp.md
 */

const VERDICT = 'Viewers leave at 0:05. Open on the reveal.';

// Sample retention: share of viewers still watching, second by second. The
// fall at 0:05 is the moment the verdict is about.
const RETENTION = [100, 97, 94, 90, 84, 52, 49, 47, 46, 45, 44, 43, 43, 42, 41, 41, 40, 40, 39, 39];

function Shot({ kind }) {
    if (kind === 'idea') {
        return <div className="home-shot home-shot-caption"><span>My 60-second morning</span></div>;
    }
    if (kind === 'carried') {
        return <div className="home-shot home-shot-caption is-carried"><span>Open on the reveal</span></div>;
    }
    if (kind === 'edit') {
        return (
            <div className="home-shot home-shot-timeline" aria-hidden="true">
                <span className="home-track is-picture" style={{ width: '88%' }} />
                <span className="home-track is-overlay" style={{ width: '34%', marginLeft: '30%' }} />
                <span className="home-track is-sound" style={{ width: '92%' }} />
                <span className="home-playhead" />
            </div>
        );
    }
    if (kind === 'post') {
        return (
            <div className="home-shot home-shot-post" aria-hidden="true">
                <PlatformMark platform="Youtube" size={26} />
                <PlatformMark platform="Instagram" size={24} />
                <span className="home-stamp sb-frame-label">Posted</span>
            </div>
        );
    }
    if (kind === 'report') {
        const points = RETENTION.map((v, i) => `${(i / (RETENTION.length - 1)) * 100},${100 - v}`).join(' ');
        return (
            <div className="home-shot home-shot-chart" aria-hidden="true">
                <svg viewBox="0 0 100 100" preserveAspectRatio="none">
                    <polyline points={points} />
                </svg>
                <span className="home-drop" style={{ left: `${(5 / (RETENTION.length - 1)) * 100}%` }}>0:05</span>
            </div>
        );
    }
    return <div className="home-shot home-shot-empty" aria-hidden="true" />;
}

const VIDEO_ONE = [
    { n: 1, title: 'Idea', note: 'Morning routine in 60 seconds', shot: 'idea' },
    { n: 2, title: 'Edit', note: '0:58 cut, two overlays', shot: 'edit' },
    { n: 3, title: 'Post', note: 'YouTube Shorts and Instagram', shot: 'post' },
    { n: 4, title: 'Report', note: VERDICT, verdict: true, shot: 'report', action: 'Start the next video' },
];

const VIDEO_TWO = [
    { n: 1, title: 'Idea', note: 'From Video 1’s report', carried: true, shot: 'carried' },
    { n: 2, title: 'Edit', note: 'Up next' },
    { n: 3, title: 'Post', note: 'Up next' },
    { n: 4, title: 'Report', note: 'Up next' },
];


function SampleStrip({ label, frames, currentIndex, className = '' }) {
    return (
        <div className={`home-strip ${className}`}>
            <h2 className="home-strip-title">{label}</h2>
            <ol className="storyboard-frames">
                {frames.map((frame, index) => {
                    const status = currentIndex === undefined || index < currentIndex
                        ? 'done'
                        : index === currentIndex ? 'current' : 'upcoming';
                    return (
                        <li key={frame.n} className={`storyboard-frame is-${status}`}>
                            <div className="storyboard-frame-button home-panel">
                                <Shot kind={frame.shot} />
                                <span className="storyboard-frame-number sb-frame-label" aria-hidden="true">{frame.n}</span>
                                <span className="storyboard-frame-title">{frame.title}</span>
                                <span
                                    className={`storyboard-frame-note${frame.verdict ? ' is-verdict' : ''}${frame.carried ? ' is-carried' : ''}`}
                                >
                                    {frame.note}
                                </span>
                                {frame.action && (
                                    <span className="home-frame-action">{frame.action}</span>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ol>
            {className.includes('is-next') && (
                <p className="home-upnext"><span className="sb-frame-label">2 · 3 · 4</span> Edit, post and report: up next</p>
            )}
        </div>
    );
}

function UnauthenticatedApp() {
    const { login } = useAuth();

    return (
        <div className="home">
            <header className="home-titleblock">
                <span className="home-wordmark" aria-label="Vixo">vi<span className="home-wordmark-x">x</span>o</span>
                <button type="button" className="btn btn-sm" onClick={login}>Sign in</button>
            </header>

            <main className="home-main">
                <section className="home-opening" aria-labelledby="home-title">
                    <h1 id="home-title" className="home-title">Every video teaches the <span className="home-title-mark">next.</span></h1>
                    <p className="home-lede">
                        Idea, edit, post, report. Then the report writes the start of your next video.
                    </p>
                    <div className="home-action">
                        <button type="button" className="btn btn-primary home-cta" onClick={login}>
                            Continue with Google
                        </button>
                        <span className="home-action-note">Pay only for what you use. No subscription.</span>
                    </div>
                </section>

                <figure className="home-demo">
                    <SampleStrip label="Video 1 (sample)" frames={VIDEO_ONE} />
                    <div className="home-connector" aria-hidden="true">
                        <div className="home-connector-reveal">
                            <svg className="home-connector-line" viewBox="0 0 100 40" preserveAspectRatio="none">
                                <path d="M 87.5 0 C 87.5 26, 12.5 14, 12.5 40" />
                            </svg>
                            <span className="home-connector-arrow" />
                        </div>
                        <span className="home-connector-narrow">
                            <ArrowDown size={22} aria-hidden="true" />
                        </span>
                    </div>
                    <SampleStrip label="Video 2 (sample)" frames={VIDEO_TWO} currentIndex={0} className="is-next" />
                    <figcaption className="sr-only">
                        A sample project. Video 1 goes from idea to report, and its report says:
                        {` ${VERDICT}`} That advice becomes the first note of Video 2&apos;s idea.
                    </figcaption>
                </figure>

                <section className="home-section home-pricing" aria-labelledby="home-pricing">
                    <h2 id="home-pricing" className="home-h2">Pay for what you use</h2>
                    <p className="home-pricing-text">
                        No subscription. Top up a balance and see what each piece of work costs.
                    </p>
                </section>

                <section className="home-close">
                    <p className="home-close-line">Start your first video. The second one gets easier.</p>
                    <button type="button" className="btn btn-primary home-cta" onClick={login}>
                        Continue with Google
                    </button>
                </section>
            </main>
        </div>
    );
}

export { UnauthenticatedApp };
